/** @jsxImportSource @emotion/react */
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { ButtonPrimary, DeleteButton } from "../../../components/NewButtons";
import { ButtonType } from "./DeleteMenuSelector";
import { DropdownItemButton } from "../../common/DropdownItemButton";
import { ConfirmActionModal } from "../../../components/ConfirmActionModal";
import * as authSelectors from "../../auth/authSelectors";
import { toastr } from "react-redux-toastr";
import { useSelector } from "react-redux";
import { useResetMenuDraft } from "../service/menus-api";
import { getErrorMessage } from "../../../error-handler";

type Props = {
  buttonType?: ButtonType;
  id?: string;
  onClick?: () => void;
};

export const ResetDraftSelector = ({ buttonType, id, onClick }: Props) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const {
    mutateAsync: resetMenu,
    isLoading: resetIsLoading,
  } = useResetMenuDraft(id);

  const handleResetUnpushedChanges = async () => {
    if (!id) return;

    try {
      await resetMenu({
        id,
        organizationKeyName,
      });
      setModalOpen(false);
      toastr.success("Success", "Unpushed changes succesfully undone");
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      toastr.error("Error", message);
    }
  };

  const openModal = () => {
    if (onClick) {
      onClick();
    }
    setModalOpen(true);
  };

  return (
    <div>
      {buttonType === "regular" ? (
        <ButtonPrimary
          onClick={openModal}
          icon={<FontAwesomeIcon icon={solid("arrow-left-rotate")} />}
          type="button"
        >
          Undo unpushed edits
        </ButtonPrimary>
      ) : (
        <DropdownItemButton
          onClick={openModal}
          icon={<FontAwesomeIcon icon={solid("arrow-left-rotate")} />}
        >
          Undo unpushed edits
        </DropdownItemButton>
      )}
      <ConfirmActionModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Are you sure?"
        subtitle="Undoing unpushed changes cannot be undone."
        confirmButton={
          <DeleteButton onClick={handleResetUnpushedChanges} type="button">
            {resetIsLoading ? (
              <FontAwesomeIcon icon={solid("spinner")} spin pulse />
            ) : (
              "Undo unpushed changes"
            )}
          </DeleteButton>
        }
      />
    </div>
  );
};
